export enum FileExtensionErrorReason {
  InvalidExtension = 'InvalidExtension',
  MissingExtension = 'MissingExtension',
}

type FileExtensionErrorConfig<R extends FileExtensionErrorReason = FileExtensionErrorReason> =
  Extract<
    | {
        readonly extension: string;
        readonly reason: FileExtensionErrorReason.InvalidExtension;
      }
    | {
        readonly extension?: never;
        readonly reason: FileExtensionErrorReason.MissingExtension;
      },
    { reason: R }
  >;

const FileExtensionErrorMessages: {
  [key in FileExtensionErrorReason]: (config: FileExtensionErrorConfig<key>) => string;
} = {
  [FileExtensionErrorReason.InvalidExtension]: config =>
    `The file extension '${config.extension}' is invalid!`,
  [FileExtensionErrorReason.MissingExtension]: () => 'The file is missing an extension!',
};

export const getFileExtensionErrorMessage = <R extends FileExtensionErrorReason>(
  config: FileExtensionErrorConfig<R>,
): string => FileExtensionErrorMessages[config.reason](config);

type FileExtensionErrorExtension<R extends FileExtensionErrorReason> =
  R extends FileExtensionErrorReason.InvalidExtension ? string : never;

export class FileExtensionError<
  R extends FileExtensionErrorReason = FileExtensionErrorReason,
> extends Error {
  public readonly extension: FileExtensionErrorExtension<R>;
  public readonly reason: R;

  private constructor(config: FileExtensionErrorConfig<R>) {
    super(getFileExtensionErrorMessage(config));
    this.name = 'FileExtensionError';
    this.reason = config.reason;
    this.extension = config.extension as FileExtensionErrorExtension<R>;
  }

  public static Invalid(
    extension: string,
  ): FileExtensionError<FileExtensionErrorReason.InvalidExtension> {
    return new FileExtensionError({ extension, reason: FileExtensionErrorReason.InvalidExtension });
  }

  public static Missing(): FileExtensionError<FileExtensionErrorReason.MissingExtension> {
    return new FileExtensionError({ reason: FileExtensionErrorReason.MissingExtension });
  }
}

const ApplicationFileExtensions = [
  '.abw',
  '.arc',
  '.azw',
  '.cda',
  '.epub',
  '.json',
  '.odp',
  '.ods',
  '.odt',
  '.ogx',
  '.pdf',
  '.rtf',
  '.xml',
] as const satisfies `.${string}`[];

const MicrosoftFileExtensions = [
  '.doc',
  '.docx',
  '.ppt',
  '.pptx',
  '.xls',
  '.xlsx',
] as const satisfies `.${string}`[];

const AudioFileExtensions = [
  '.aac',
  '.mid',
  '.midi',
  '.mp3',
  '.oga',
  '.opus',
  '.wav',
  '.weba',
] as const satisfies `.${string}`[];

const ImageFileExtensions = [
  '.avif',
  '.bmp',
  '.gif',
  '.ico',
  '.jpeg',
  '.jpg',
  '.png',
  '.svg',
  '.tif',
  '.tiff',
  '.webp',
] as const satisfies `.${string}`[];

const TextFileExtensions = ['.csv', '.ics', '.txt'] as const satisfies `.${string}`[];

const VideoFileExtensions = [
  '.avi',
  '.mp4',
  '.mpeg',
  '.ogv',
  '.webm',
] as const satisfies `.${string}`[];

export const FileExtensions = [
  ...ApplicationFileExtensions,
  ...MicrosoftFileExtensions,
  ...AudioFileExtensions,
  ...ImageFileExtensions,
  ...TextFileExtensions,
  ...VideoFileExtensions,
] as const;

export type FileExtension = (typeof FileExtensions)[number];

export const isFileExtension = (value: unknown): value is FileExtension =>
  FileExtensions.includes(value as FileExtension);

interface SanitizeFileExtensionOptions {
  /**
   * Determines whether or not an error, {@link FileExtensionError}, should be thrown if the file
   * extension cannot be sanitized. If the 'strict' option is 'false', the method will instead
   * return an object, {@link NonStrictSanitizedFile}, that contains either a copy of the file with
   * the successfully sanitized extension, {@link File}, or an error, {@link FileExtensionError}, in
   * the case the extension cannot be sanitized.
   *
   * @default true
   */
  readonly strict?: boolean;
}

type NonStrictSanitizedFile =
  | { readonly error: FileExtensionError; readonly sanitizedFile?: never }
  | { readonly error?: never; readonly sanitizedFile: File };

/**
 * Represents the return of the {@link sanitizeFileExtension} method that is conditional based on
 * the options, {@link SanitizeFileExtensionOptions}, that are provided to the method.
 */
type SanitizedFile<O extends SanitizeFileExtensionOptions> = O extends {
  readonly strict: false;
}
  ? NonStrictSanitizedFile
  : File;

interface ParseFileExtensionOptions {
  /**
   * Determines whether or not an error, {@link FileExtensionError}, should be thrown if the file
   * extension cannot be parsed. If the 'strict' option is 'false', the method will instead return
   * an object, {@link NonStrictParsedFileExtension}, that contains either the successfully parsed
   * extension, {@link FileExtension}, or an error, {@link FileExtensionError}, in the case the
   * extension cannot be parsed.
   *
   * @default true
   */
  readonly strict?: boolean;
}

type NonStrictParsedFileExtension =
  | { readonly error: FileExtensionError; readonly extension?: never }
  | { readonly error?: never; readonly extension: FileExtension };

/**
 * Represents the return of the {@link parseFileExtension} method that is conditional based on the
 * options, {@link ParseFileExtensionOptions}, that are provided to the method.
 */
type ParsedFileExtension<O extends ParseFileExtensionOptions> = O extends { readonly strict: false }
  ? NonStrictParsedFileExtension
  : FileExtension;

/**
 * Parses the file extension from the provided file name, {@link string}.
 *
 * @param {string | File} file
 *   The name of the file, or the file object itself, {@link File}, for which the extension is being
 *   parsed.
 *
 * @throws {FileExtensionError}
 *   An error, {@link FileExtensionError}, if the file extension cannot be parsed and the 'strict'
 *   option is not explicitly false.
 *
 * @returns {ParsedFileExtension<O>}
 *   The file extension, {@link FileExtension}, in the case that the 'strict' option is 'true'
 *   (which it is, by default). If the 'strict' option is 'false', the method will return an
 *   object, {@link NonStrictParsedFileExtension}, that either contains the successfully parsed
 *   extension, {@link FileExtension}, or the error in the case that the extension could not be
 *   parsed, {@link FileExtensionError}.
 */
export const parseFileExtension = <O extends ParseFileExtensionOptions>(
  file: File | string,
  options?: O,
): ParsedFileExtension<O> => {
  const fileName = typeof file === 'string' ? file : file.name;

  let ext: string | undefined = undefined;
  if (fileName.includes('.')) {
    ext = fileName.split('.').pop();
  }
  if (ext === undefined || ext.trim().length === 0) {
    if (options?.strict === false) {
      return { error: FileExtensionError.Missing() } as ParsedFileExtension<O>;
    }
    throw FileExtensionError.Missing();
  }
  const fullExtension = `.${ext.trim()}`;
  if (!isFileExtension(fullExtension)) {
    if (options?.strict === false) {
      return { error: FileExtensionError.Invalid(fullExtension) } as ParsedFileExtension<O>;
    }
    throw FileExtensionError.Invalid(fullExtension);
  }
  if (options?.strict === false) {
    return { extension: fullExtension } as ParsedFileExtension<O>;
  }
  return fullExtension as ParsedFileExtension<O>;
};

/**
 * Takes the provided file and splits out the extension. It then converts the extension to lowercase
 * before rejoining it to the original file name and creating a new file object from the sanitized
 * result. As we validate all file extensions against a list that is case sensitive, and the file
 * name (including the extension) that is uploaded is also case sensitive, it is important that we
 * sanitize the extension so that we can properly validate if the extension is contained in our list
 * of valid extensions.
 *
 * @param {File} file The file, {@link File}, for which the extension is being sanitized.
 *
 * @returns {SanitizedFile<O>}
 *   Newly constructed file object, built from the original file and sanitized extension.
 */
export const sanitizeFileExtension = <O extends SanitizeFileExtensionOptions>(
  file: File,
  options?: O,
): SanitizedFile<O> => {
  let splitFileName;
  let ext;
  if (file.name.includes('.')) {
    splitFileName = file.name.split('.');
    ext = splitFileName.pop();
  }
  if (!splitFileName || !ext || ext.trim().length === 0) {
    if (options?.strict === false) {
      return { error: FileExtensionError.Missing() } as SanitizedFile<O>;
    }
    throw FileExtensionError.Missing();
  }

  const sanitizedFile = new File([file], `${splitFileName.join('.')}.${ext.trim().toLowerCase()}`, {
    type: file.type,
  });

  if (options?.strict === false) {
    return { sanitizedFile } as SanitizedFile<O>;
  }
  return sanitizedFile as SanitizedFile<O>;
};
